<script setup>
import { defineProps, defineEmits, watch, ref, computed} from "vue";
import UtilMoney from "@/utils/UtilMoney";
import UtilNumber from "@/utils/UtilNumber";
import UtilRules from "@/utils/UtilRules.mjs";
import NotaFiscalService from "@/api/v2/services/NotaFiscalService";

//==================================================================//
//========================= Props & Emits ==========================//
//==================================================================//

const props = defineProps([
    "vincularNotaFiscal",
    "controlesDesativados",
    "notasFiscaisVinculaveis",
    "dado",
    "recebimentoNovo",
    "contaAReceberValorOriginal",
    "contaAReceberValorVinculado",
    "contaAReceberSaldoAVincular",
    "notaFiscalEmitidaValorOriginal",
    "notaFiscalEmitidaValorRecebido",
    "notaFiscalEmitidaValorParaReceber",
]);

const emits = defineEmits([
    "update:alteraControlesDesativados",
    "update:alteraRecebimentoNovo",
    "update:autocalculaRecebimentoNovo"
]);

//==================================================================//
//=================== REF Nota Fiscal Emitida ======================//
//==================================================================//

const refNotaFiscalEmitida = ref("");

//==================================================================//
//================= Altera Controles Desativados ===================//
//==================================================================//

const alteraControlesDesativados = (update)=>{

    emits('update:alteraControlesDesativados',update);

};

//==================================================================//
//=================== Altera Recebimento Novo ======================//
//==================================================================//

const alteraRecebimentoNovo = (update)=>{

    emits('update:alteraRecebimentoNovo',update);

};

//==================================================================//
//================= Autocalcula Recebimento Novo ===================//
//==================================================================//

const autocalculaRecebimentoNovo = (update)=>{

    emits('update:autocalculaRecebimentoNovo',update);

};

//==================================================================//
//============ Cor Conta a Receber Valor Vinculado =================//
//==================================================================//

const corContaAReceberValorVinculado = computed(()=>{

    if(props?.contaAReceberValorVinculado >= 0 && props?.contaAReceberValorVinculado <= props?.contaAReceberValorOriginal){
        return "green";
    }

    return "red";

});

//==================================================================//
//============= Cor Conta a Receber Saldo a Vincular ===============//
//==================================================================//

const corContaAReceberSaldoAVincular = computed(()=>{

    if(props?.contaAReceberSaldoAVincular >= 0 && props?.contaAReceberSaldoAVincular <= props?.contaAReceberValorOriginal){
        return "green";
    }

    return "red";

});

//==================================================================//
//============ Cor Nota Fiscal Emitida Valor Recebido ==============//
//==================================================================//

const corNotaFiscalEmitidaValorRecebido = computed(()=>{

    if(props?.notaFiscalEmitidaValorRecebido >= 0 && props?.notaFiscalEmitidaValorRecebido <= props?.notaFiscalEmitidaValorOriginal){
        return "green";
    }

    return "red";

});

//==================================================================//
//========= Cor Nota Fiscal Emitida Valor para Receber =============//
//==================================================================//

const corNotaFiscalEmitidaValorParaReceber = computed(()=>{

    if(props?.notaFiscalEmitidaValorParaReceber >= 0 && props?.notaFiscalEmitidaValorParaReceber <= props?.notaFiscalEmitidaValorOriginal){
        return "green";
    }

    return "red";

});

//==================================================================//
//==================== Desativa Autocalcular =======================//
//==================================================================//

const desativaAutocalcular = computed(()=>{

    if(props.controlesDesativados){
        return true;
    }

    if(!props?.recebimentoNovo?.notaFiscalEmitida?.id){
        return true;
    }

    return false;

});

//==================================================================//
//================= Desativa Nota Fiscal Emitida ===================//
//==================================================================//

const desativaNotaFiscalEmitida = computed(()=>{

    if(props.controlesDesativados){
        return true;
    }

    if(props?.recebimentoNovo?.autocalculado){
        return true;
    }

    return false;

});

//==================================================================//
//=============== Desativa Porcentagem Recebimento==================//
//==================================================================//

const desativaPorcentagemRecebimento = computed(()=>{

    if(props.controlesDesativados){
        return true;
    }

    if(props?.recebimentoNovo?.autocalculado){
        return true;
    }

    if(!props?.recebimentoNovo?.notaFiscalEmitida?.id){
        return true;
    }

    return false;

});

//==================================================================//
//============= Ao Selecionar Nota Fiscal Emitida ==================//
//==================================================================//

const aoSelecionarNotaFiscalEmitida = (notaFiscalEmitida = {})=>{

    alteraRecebimentoNovo({
        notaFiscalEmitida: notaFiscalEmitida || {},
        autocalculado: false,
        porcentagemRecebimento: 0,
        valorRecebimento: 0,
    });

};

//==================================================================//
//=============== Ao Baixar Nota Fiscal Emitida ====================//
//==================================================================//

const aoBaixarNotaFiscalEmitida = async ()=>{

    alteraControlesDesativados(true);

    let notaFiscalResponse = await NotaFiscalService.getNotaFiscalByPlugNotasId({
        nomeArquivo: props?.recebimentoNovo?.notaFiscalEmitida?.chaveAcesso,
        plugNotasId: props?.recebimentoNovo?.notaFiscalEmitida?.plugNotasID
    });
    if(notaFiscalResponse.status != "200"){
        throw new Error(`${notaFiscalResponse.message}`)
    }

    alteraControlesDesativados(false);

};

//==================================================================//
//============= Ao Alterar Porcentagem Recebimento =================//
//==================================================================//

const aoAlterarPorcentagemRecebimento = (porcentagemRecebimento = {})=>{

    alteraRecebimentoNovo({
        porcentagemRecebimento: UtilNumber.toNumber(porcentagemRecebimento) || 0,
    });

};

//==================================================================//
//===================== Ao Autocalcular ============================//
//==================================================================//

const aoAutocalcular = () =>{

    autocalculaRecebimentoNovo({
        autocalculado: !props?.recebimentoNovo?.autocalculado,
        porcentagemRecebimento: 0,
        valorRecebimento: 0,
    })

};

//==================================================================//
//================== Watch => vincularNotaFiscal ===================//
//==================================================================//

watch(()=> props.vincularNotaFiscal, async () => {

    if(props.vincularNotaFiscal == false){

        refNotaFiscalEmitida.value.reset();

    }

});

</script>

<template>

    <v-card class="c-cartao">

        <v-card-title class="c-cartao__header">

            <h3 class="c-header__titulo">

                Adiantamento {{ props?.dado?.numero }}

            </h3>

            <div class="c-header__controles">

                <v-btn
                    class="c-botao-autocalcular"
                    color="primary"
                    dark
                    :disabled="desativaAutocalcular"
                    @click="aoAutocalcular"
                >
                    
                    <v-simple-checkbox
                        class="c-botao-autocalcular__checkbox"
                        :value="props?.recebimentoNovo?.autocalculado"
                        :disabled="desativaAutocalcular"
                    ></v-simple-checkbox>

                    Autocalcular

                </v-btn>

            </div>

            <div class="c-header__medidores c-medidores">

                <div class="c-medidor">

                    <v-chip class="c-medidor__chip"
                        color="green"
                        dark
                        label
                    >

                        CA - Valor Original:
                        {{UtilMoney.toR$BRL(props?.contaAReceberValorOriginal)}}

                    </v-chip>

                </div>

                <div class="c-medidor">

                    <v-chip class="c-medidor__chip"
                        :color="corContaAReceberValorVinculado"
                        dark
                        label
                    >

                        CA - Valor Vinculado:
                        {{UtilMoney.toR$BRL(props?.contaAReceberValorVinculado)}}

                    </v-chip>

                </div>

                <div class="c-medidor">

                    <v-chip class="c-medidor__chip"
                        :color="corContaAReceberSaldoAVincular"
                        dark
                        label
                    >

                        CA - Saldo a Vincular:
                        {{UtilMoney.toR$BRL(props?.contaAReceberSaldoAVincular)}}

                    </v-chip>

                </div>

            </div>

        </v-card-title>

        <v-divider></v-divider>

        <div class="c-cartao__body">

            <div class="c-campos c-campos--flex">

                <v-tooltip bottom>

                    <template v-slot:activator="{ on, attrs }">

                        <v-btn
                            v-show="props?.recebimentoNovo?.notaFiscalEmitida?.id"
                            class="c-baixar-nota-fiscal-emitida mx-2"
                            fab
                            dark
                            small
                            color="#3E737B"
                            v-bind="attrs"
                            v-on="on"
                            @click="aoBaixarNotaFiscalEmitida"
                        >

                            <v-icon dark>
                                mdi-download
                            </v-icon>
                        
                        </v-btn>

                    </template>

                    <span>Baixar Nota Fiscal Emitida</span>

                </v-tooltip>

                <v-autocomplete
                    ref="refNotaFiscalEmitida"
                    label="Nota Fiscal Emitida"
                    :items="props?.notasFiscaisVinculaveis"
                    :disabled="desativaNotaFiscalEmitida"
                    outlined
                    clearable
                    return-object
                    hide-details
                    item-text="numero"
                    @change="aoSelecionarNotaFiscalEmitida"
                >

                    <template v-slot:item="{ item }">
                        <span><strong>{{item.sigla}}: </strong><span>{{item.numero}}</span></span>
                    </template>

                    <template v-slot:selection="{ item }">
                        <span><strong>{{item.sigla}}: </strong><span>{{item.numero}}</span></span>
                    </template>

                </v-autocomplete>

            </div>

            <div class="c-campos c-campos--flex">

                <div class="c-medidores">

                    <div class="c-medidor">

                        <v-chip class="c-medidor__chip"
                            color="green"
                            dark
                            label
                        >

                            NF - Valor Original:
                            {{UtilMoney.toR$BRL(props?.notaFiscalEmitidaValorOriginal)}}

                        </v-chip>

                    </div>

                    <div class="c-medidor">

                        <v-chip class="c-medidor__chip"
                            :color="corNotaFiscalEmitidaValorRecebido"
                            dark
                            label
                        >

                            NF - Valor Recebido:
                            {{UtilMoney.toR$BRL(props?.notaFiscalEmitidaValorRecebido)}}

                        </v-chip>

                    </div>

                    <div class="c-medidor">

                        <v-chip class="c-medidor__chip"
                            :color="corNotaFiscalEmitidaValorParaReceber"
                            dark
                            label
                        >

                            NF - Valor para Receber:
                            {{UtilMoney.toR$BRL(props?.notaFiscalEmitidaValorParaReceber)}}

                        </v-chip>

                    </div>

                </div>

            </div>

            <v-divider></v-divider>

            <div class="c-campos c-campos--grid-fit-columns">

                <vuetify-money
                    class="c-money"
                    label="Porcentagem Vinculo"
                    type="number"
                    outlined
                    hide-details
                    :value="props?.recebimentoNovo?.porcentagemRecebimento"
                    :disabled="desativaPorcentagemRecebimento"
                    :rules="UtilRules.percentage"
                    :options="{ 
                        'precision': '2'
                    }"
                    :properties="{
                        'class':'c-campo__input',
                        'prepend-icon':'mdi-percent',
                        
                    }"
                    @input="aoAlterarPorcentagemRecebimento"
                ></vuetify-money>

                <vuetify-money
                    class="c-money"
                    type="number"
                    outlined
                    :value="props?.recebimentoNovo?.valorRecebimento"
                    label="Valor Recebimento"
                    disabled
                    hide-details
                    :properties="{ 
                        'class':'c-campo__input',
                        'prepend-icon':'mdi-currency-brl'
                    }"
                ></vuetify-money>

            </div>

        </div>

    </v-card>

</template>

<style scoped>

/*==================================================================*/
/*========================== Cartão ================================*/
/*==================================================================*/

.c-cartao__header{
    display: grid; 
    grid-template-columns: 1fr max-content; 
    grid-template-rows: max-content max-content; 
    gap: 10px; 
    grid-template-areas: 
        "titulo controles"
        "medidores medidores";
}

.c-header__titulo {
    grid-area: titulo;
}

.c-header__controles {
    grid-area: controles;
}

.c-header__medidores{
    grid-area: medidores;
}

.c-cartao__body{
    display: grid;
    grid-gap: 15px;
}

/*==================================================================*/
/*=========================== Botão ================================*/
/*==================================================================*/

.theme--dark.v-btn.v-btn--disabled.v-btn--has-bg.c-botao-autocalcular{
    background: #3F51B5 !important;
    border-color: #3F51B5 !important;
}

.c-botao-autocalcular__checkbox{
    pointer-events: none;
}

/*==================================================================*/
/*========================= Medidores ==============================*/
/*==================================================================*/

.c-medidores{
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

/*==================================================================*/
/*========================== Campos ================================*/
/*==================================================================*/

.c-campos--flex{
    display: flex;
}

.c-campos--grid-fit-columns{
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
    grid-gap: 20px;
}

/*==================================================================*/
/*=================== Baixar Nota Fiscal Emitida ===================*/
/*==================================================================*/

.theme--dark.v-btn.v-btn--disabled.v-btn--has-bg.c-baixar-nota-fiscal-emitida{
    background: #3E737B !important;
    border-color: #3E737B !important;
}

</style>